import { motion } from "framer-motion";
import { Box, Typography, Grid, Stack } from "@mui/material";
import { Target, ShieldCheck, Recycle, Award } from "lucide-react";
import { useSEO } from "../lib/seo";
import { Reveal } from "../site/Reveal";
import { CTASection } from "../site/CTASection";
/**
 * NOTE ON COLOR TOKENS
 * ---------------------------------------------------------------------
 * The original file used Tailwind theme aliases (ink, safety, obsidian,
 * concrete, muted-foreground) that are defined in a tailwind.config, not
 * in the component itself. Since those hex values aren't present in the
 * source, they're reproduced here as a local token map so the visual
 * result matches. Swap these hex values for your real design-token
 * values if they differ.
 */
const COLORS = {
  ink: "#161616",
  safety: "#ff5a1f",
  obsidian: "#0b0b0c",
  concrete: "#f2f1ee",
  white: "#ffffff",
  mutedForeground: "rgba(22, 22, 22, 0.6)",
  whiteMuted: "rgba(255, 255, 255, 0.6)",
  borderInk15: "rgba(22, 22, 22, 0.15)",
};

const STANDARDS = [
  ["ISI 9815:1994", "Servo controlled AC voltage stabilizers"],
  ["IS:2026", "Power & isolation transformers"],
  ["IS:335", "Transformer insulating oil"],
];

const VALUES = [
  {
    icon: Target,
    t: "Efficiency",
    d: "Designs that minimise losses and deliver up to 90–95% efficiency across the load range.",
  },
  {
    icon: ShieldCheck,
    t: "Protection",
    d: "Guarding your electrical assets against voltage swings, spikes and line noise.",
  },
  {
    icon: Recycle,
    t: "Long Service Life",
    d: "Rugged construction and quality materials for years of trouble-free operation.",
  },
  {
    icon: Award,
    t: "Compliance",
    d: "Every build documented and manufactured to the relevant Indian standard.",
  },
];

// Reusable mono-label styling used above section eyebrows ("[ ... ]")
const monoLabelSx = {
  fontFamily: "monospace",
  fontSize: "0.8125rem",
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: COLORS.safety,
};

export default function AboutUsPage() {
  useSEO({
    title:
      "About Us | ITL Technical Services — Voltage Stabilizer Manufacturer",
    description:
      "Learn about ITL Technical Services — our manufacturing philosophy, standards compliance (ISI 9815:1994, IS:2026, IS:335) and mission to protect electrical equipment through precision power conditioning.",
  });

  return (
    <>
      {/* HEADER */}
      <Box
        data-testid="about-header"
        sx={{
          position: "relative",
          color: "#fff",
          overflow: "hidden",
          pt: { xs: 16, md: 22 },
          pb: { xs: 8, md: 12 },

          backgroundImage: `
      linear-gradient(
        rgba(10,10,10,0.75),
        rgba(10,10,10,0.82)
      ),
      url("/AboutUsBanner.jpg")
    `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Box
          sx={{
            maxWidth: "1280px",
            mx: "auto",
            px: { xs: 2, sm: 3, lg: 4 },
            position: "relative",
            mt: 4,
            mb: 4,
          }}
        >
          <Typography
            component="p"
            sx={{
              ...monoLabelSx,
              fontSize: "1rem",
              mb: 4,
              textAlign: "left",
            }}
          >
            [ About us ]
          </Typography>
          <Typography
            component={motion.h1}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            sx={{
              fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
              fontWeight: 900,
              fontSize: { xs: "3rem", md: "4.5rem" },
              letterSpacing: "-0.04em",
              lineHeight: 0.85,
              maxWidth: "56rem",
              textAlign: "left",
            }}
          >
            Decades of manufacturing precision.
          </Typography>
        </Box>
      </Box>

      {/* INTRO */}
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 8, md: 14 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 6, md: 6 },
            alignItems: "stretch",
          }}
        >
          {/* Content: 60% */}
          <Box
            sx={{
              width: { xs: "100%", md: "60%" },
              flexShrink: 0,
            }}
          >
            <Reveal>
              <Stack spacing={3}>
                <Typography
                  sx={{
                    color: COLORS.ink,
                    fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                    fontSize: { xs: "1.5rem", md: "1.875rem" },
                    lineHeight: 1.375,
                    letterSpacing: "-0.02em",
                    textAlign: "left",
                  }}
                >
                  ITL Technical Services is a manufacturer and supplier of
                  industrial power-conditioning equipment — built for engineers
                  who care about specifications, durability and after-sales
                  service.
                </Typography>
                <Typography
                  sx={{
                    color: COLORS.mutedForeground,
                    fontSize: "1.125rem",
                    lineHeight: 1.75,
                    textAlign: "left",
                  }}
                >
                  We design, manufacture and service servo controlled automatic
                  AC voltage stabilizers, super isolation / noise cut-off
                  transformers and constant voltage transformers, alongside a
                  general supply of electrical and electronic equipment. Our
                  manufacturing philosophy is simple: engineer for reliability
                  first, then optimise for efficiency and cost.
                </Typography>
                <Typography
                  sx={{
                    color: COLORS.mutedForeground,
                    fontSize: "1.125rem",
                    lineHeight: 1.75,
                    textAlign: "left",
                  }}
                >
                  Every product is built to conform with the relevant Indian
                  standards, using high-quality lamination and enameled
                  electrolytic copper windings that meet international
                  benchmarks — because protecting your equipment starts with the
                  quality of ours.
                </Typography>
              </Stack>
            </Reveal>
          </Box>

          {/* Image: 40% */}
          <Box
            sx={{
              width: { xs: "100%", md: "40%" },
              flexShrink: 0,
            }}
          >
            <Reveal delay={0.15}>
              <Box
                component="img"
                src="https://images.pexels.com/photos/19544217/pexels-photo-19544217.jpeg"
                alt="Industrial plant engineer inspecting electrical equipment in a warehouse"
                loading="lazy"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  filter: "grayscale(1)",
                  border: `1px solid ${COLORS.borderInk15}`,
                }}
              />
            </Reveal>
          </Box>
        </Box>
      </Box>

      {/* MISSION VALUES */}
      <Box
        sx={{
          bgcolor: COLORS.concrete,
          borderTop: `1px solid ${COLORS.borderInk15}`,
          borderBottom: `1px solid ${COLORS.borderInk15}`,
        }}
      >
        <Box
          sx={{
            maxWidth: "1280px",
            mx: "auto",
            px: { xs: 2, sm: 3, lg: 4 },
            py: { xs: 8, md: 12 },
          }}
        >
          <Reveal>
            <Box sx={{ mb: 7 }}>
              <Typography
                component="p"
                sx={{ ...monoLabelSx, mb: 3, textAlign: "left" }}
              >
                [ Our mission ]
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                  fontWeight: 900,
                  fontSize: { xs: "1.875rem", md: "3rem" },
                  letterSpacing: "-0.04em",
                  color: COLORS.ink,
                  maxWidth: "48rem",
                  textAlign: "left",
                }}
              >
                Efficiency, protection and long service life.
              </Typography>
            </Box>
          </Reveal>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                lg: "1fr 1fr 1fr 1fr",
              },
              gap: "1px",
              bgcolor: COLORS.borderInk15,
              border: `1px solid ${COLORS.borderInk15}`,
            }}
          >
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.t} delay={i * 0.08}>
                  <Box sx={{ bgcolor: COLORS.white, height: "100%" }}>
                    <Box
                      sx={{
                        p: 4,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <Icon
                        style={{
                          width: 32,
                          height: 32,
                          color: COLORS.safety,
                          marginBottom: "1.5rem",
                          strokeWidth: 1.75,
                        }}
                      />

                      <Typography
                        component="h3"
                        sx={{
                          fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                          fontWeight: 700,
                          fontSize: "1.25rem",
                          color: COLORS.ink,
                        }}
                      >
                        {v.t}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          color: COLORS.mutedForeground,
                          mt: 1,
                          lineHeight: 1.625,
                          textAlign: "left",
                        }}
                      >
                        {v.d}
                      </Typography>
                    </Box>
                  </Box>
                </Reveal>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* STANDARDS */}
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 8, md: 12 },
        }}
      >
        <Reveal>
          <Box sx={{ mb: 6 }}>
            <Typography
              component="p"
              sx={{
                ...monoLabelSx,
                mb: 3,
                textAlign: "left",
              }}
            >
              [ Standards compliance ]
            </Typography>

            <Typography
              component="h2"
              sx={{
                fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                fontWeight: 900,
                fontSize: { xs: "1.875rem", md: "3rem" },
                letterSpacing: "-0.04em",
                color: COLORS.ink,
                textAlign: "left",
              }}
            >
              Built to Indian standards.
            </Typography>
          </Box>
        </Reveal>

        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
          }}
        >
          <Grid
            container
            spacing={3}
            alignItems="stretch"
            justifyContent="center"
          >
            {STANDARDS.map(([code, desc], i) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={code}
                sx={{
                  display: "flex",
                }}
              >
                <Reveal delay={i * 0.08}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      p: 4,
                      minWidth: "340px",
                      border: `1px solid ${COLORS.borderInk15}`,
                      bgcolor: COLORS.obsidian,
                      color: COLORS.white,
                      position: "relative",

                      "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        opacity: 0.06,
                        pointerEvents: "none",
                        backgroundImage:
                          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                      },
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: { xs: "1.5rem", md: "1.875rem" },
                        fontWeight: 700,
                        color: COLORS.safety,
                        display: "block",
                      }}
                    >
                      {code}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 2,
                        color: COLORS.whiteMuted,
                        fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                        lineHeight: 1.7,
                      }}
                    >
                      {desc}
                    </Typography>
                  </Box>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      <CTASection />
    </>
  );
}
